// @mui
import { Box, Drawer } from '@mui/material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// components
import AccountPopover from '../header/AccountPopover';
import Logo from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';

// config
import useNavConfig from './config';

// ----------------------------------------------------------------------

const NAV_WIDTH = 260;
const NAV_COLLAPSED_WIDTH = 80;

// ----------------------------------------------------------------------

interface NavProps {
	openNav: boolean;
	onCloseNav: () => void;
}

export default function Nav({ openNav, onCloseNav }: NavProps) {
	const navConfig = useNavConfig();

	const isDesktop = useResponsive({
		query: 'up',
		start: 'lg',
	});

	const isTablet = useResponsive({
		query: 'between',
		start: 'sm',
		end: 'lg',
	});

	const isCollapsed = isTablet && !isDesktop;

	const renderContent = (
		<Scrollbar
			sx={{
				height: 1,
				background: '#ffffff',
				'& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
			}}
		>
			<Box
				sx={{
					height: 1,
					display: 'flex',
					flexDirection: 'column',
					width: 1,
				}}
			>
				<Box
					sx={{
						px: isCollapsed ? 1.5 : 2.5,
						py: 3,
						width: 1,
					}}
				>
					<Logo isCollapsed={isCollapsed} />
				</Box>

				<NavSection data={navConfig} collapsed={isCollapsed} />

				<Box sx={{ flexGrow: 1 }} />

				<Box
					sx={{
						px: isCollapsed ? 1.5 : 2.5,
						py: 2,
						display: 'flex',
						justifyContent: isCollapsed ? 'center' : 'flex-start',
						width: 1,
					}}
				>
					<AccountPopover isCollapsed={isCollapsed} />
				</Box>
			</Box>
		</Scrollbar>
	);

	return (
		<Box
			component="nav"
			sx={{
				flexShrink: { sm: 0 },
				width: { sm: isCollapsed ? NAV_COLLAPSED_WIDTH : NAV_WIDTH },
			}}
		>
			{isDesktop || isCollapsed ? (
				<Drawer
					open
					variant="permanent"
					PaperProps={{
						sx: {
							width: isCollapsed ? NAV_COLLAPSED_WIDTH : NAV_WIDTH,
							bgcolor: 'background.default',
							borderRight: 'none',
						},
					}}
				>
					{renderContent}
				</Drawer>
			) : (
				<Drawer
					open={openNav}
					onClose={onCloseNav}
					ModalProps={{
						keepMounted: true,
					}}
					PaperProps={{
						sx: { width: NAV_WIDTH },
						borderRight: 'none',
					}}
				>
					{renderContent}
				</Drawer>
			)}
		</Box>
	);
}
